/**
 * Composant Section Editor — panneau collapsible avec bandeau accent_color,
 * nom éditable, sous-total et boutons d'action. Rendu à l'intérieur du QuoteEditorPage.
 *
 * Reçoit :
 *  - section : string (nom)
 *  - lines : QuoteLine[] appartenant à cette section
 *  - accentColor : couleur de la company
 *  - isDraft : bool
 *  - onRenameSection(oldName, newName)
 *  - onRemoveSection(name)
 *  - children : le rendu des lignes (composé côté parent)
 */
import { useState } from "react";
import { ChevronDown, ChevronRight, Trash2, Edit2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { formatCurrencyBE } from "@/lib/currency";

export default function SectionEditor({
  section,
  lines,
  accentColor,
  isDraft,
  onRenameSection,
  onRemoveSection,
  children,
  dragHandle,
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(section);

  const subtotal = lines
    .filter((l) => !l.is_note && !l.is_option)
    .reduce((sum, l) => sum + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0), 0);
  const optionCount = lines.filter((l) => l.is_option).length;

  const commitRename = () => {
    const next = renameValue.trim();
    if (next && next !== section) onRenameSection(section, next);
    setRenaming(false);
  };

  return (
    <div
      className="mb-3 overflow-hidden rounded-lg border border-slate-800 bg-slate-900/30"
      data-testid={`section-block-${section}`}
    >
      {/* Bandeau section */}
      <div
        className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-white"
        style={{ backgroundColor: accentColor }}
      >
        {dragHandle}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="rounded p-0.5 hover:bg-black/20"
          data-testid={`section-toggle-${section}`}
          aria-label={collapsed ? "Déplier" : "Replier"}
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
        {renaming && isDraft ? (
          <Input
            autoFocus
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => e.key === "Enter" && commitRename()}
            className="h-7 max-w-xs border-white/20 bg-white/10 text-white placeholder-white/60"
            data-testid={`section-rename-input-${section}`}
          />
        ) : (
          <span className="flex-1 truncate">
            {section} <span className="ml-1 text-xs font-normal opacity-80">({lines.length})</span>
          </span>
        )}
        {isDraft && !renaming && (
          <>
            <button
              onClick={() => {
                setRenameValue(section);
                setRenaming(true);
              }}
              className="rounded p-1 hover:bg-black/20"
              title="Renommer la section"
              data-testid={`section-rename-btn-${section}`}
            >
              <Edit2 className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => {
                if (window.confirm(`Supprimer la section "${section}" et ses ${lines.length} ligne(s) ?`)) {
                  onRemoveSection(section);
                }
              }}
              className="rounded p-1 hover:bg-black/20"
              title="Supprimer la section"
              data-testid={`section-remove-btn-${section}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </>
        )}
        <span className="tabular ml-auto text-xs font-semibold opacity-95" data-testid={`section-subtotal-${section}`}>
          {formatCurrencyBE(subtotal)}
          {optionCount > 0 && (
            <span className="ml-2 rounded bg-black/30 px-1.5 py-0.5 text-[10px] font-normal">
              +{optionCount} option{optionCount > 1 ? "s" : ""}
            </span>
          )}
        </span>
      </div>

      {!collapsed && <div className="space-y-2 p-3">{children}</div>}
    </div>
  );
}
